import React from 'react';
import { getAccessToken } from './components/AuthProvider'
import { createRoot } from 'react-dom/client';
import App from './App';
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { msalConfig } from './authConfig';
import { logger } from './utils/logger';

import 'bootstrap/dist/css/bootstrap.min.css';
import './styles/index.css';
import './styles/base.css';
import './styles/tokens/semantic.css';
import './styles/tokens/brand-as.css';

/**
 * MSAL should be instantiated outside of the component tree to prevent it from being re-instantiated on re-renders.
 * For more, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
const msalInstance = new PublicClientApplication(msalConfig);

// Prevent multiple initialization attempts
let isInitializing = false;
let initializationPromise = null;

// Default to using the first account if no account is active on page load
if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
    // Account selection logic is app dependent. Adjust as needed for different use cases.
    msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
    
}

// Listen for sign-in event and set active account
msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload.account) {
        const account = event.payload.account;
        msalInstance.setActiveAccount(account);
    }
});

// Initialize MSAL with proper error handling and prevent loops
const initializeMsal = async () => {
    if (isInitializing) {
        return initializationPromise;
    }
    
    isInitializing = true;
    initializationPromise = msalInstance.initialize()
        .then(() => {
            logger.log('MSAL initialized successfully');
            return msalInstance;
        })
        .catch((e) => {
            logger.error('Failed to initialize MSAL', e);
            // Reset initialization flag on failure to allow retry
            isInitializing = false;
            initializationPromise = null;
            throw e;
        })
        .finally(() => {
            isInitializing = false;
        });
    
    return initializationPromise;
};

// Activate the AugustSays theme. You can swap this or make it dynamic later.
document.body.classList.add('theme-as');

const root = createRoot(document.getElementById('root'));

// Ensure MSAL is initialized before using it anywhere (required in MSAL v3)
initializeMsal()
    .then(() => {
        root.render(<App instance={msalInstance} />);
        // Expose getAccessToken globally for non‑React contexts (e.g., canvasses.js)
        window.getAccessToken = (customScope) =>
            getAccessToken(msalInstance, customScope ? [customScope] : []);
    })
    .catch((e) => {
        logger.error('Failed to initialize MSAL', e);
        root.render(
            <div style={{ padding: 16 }}>
                <h3>Initialization Error</h3>
                <pre>{String(e)}</pre>
            </div>
        );
    });